import { parseISO, format } from "date-fns";
import { DAYS } from "app/utils/constants/settings";

/**
 * Adaptér: DSOK denné (týždenné) menu → `week` tvar, ktorý očakáva LunchMenuPrintView
 * (prevzaté z referenčného balíka lunch-menu-print). Plus SK default texty (alergény/info)
 * a názov listu podľa typu menu.
 */

const DAY_NAMES_SK = {
  monday: "Pondelok",
  tuesday: "Utorok",
  wednesday: "Streda",
  thursday: "Štvrtok",
  friday: "Piatok",
  saturday: "Sobota",
  sunday: "Nedeľa",
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  try {
    return parseISO(String(value));
  } catch (_) {
    return null;
  }
};

/** Váha (kg) → "300 g", inak objem (l) → "0.5 l". Prázdny string ak nie je. */
function formatWeightVolume(product) {
  const w =
    typeof product?.weightKg === "number"
      ? product.weightKg
      : parseFloat(product?.weightKg);
  if (Number.isFinite(w) && w > 0) return `${Math.round(w * 1000)} g`;
  const v =
    typeof product?.volumeLiters === "number"
      ? product.volumeLiters
      : parseFloat(product?.volumeLiters);
  if (Number.isFinite(v) && v > 0) return `${parseFloat(v.toFixed(2))} l`;
  return "";
}

/** Alergény → "1,3,7". */
function formatAllergens(allergens) {
  if (!Array.isArray(allergens) || allergens.length === 0) return "";
  return allergens
    .map((a) =>
      a && typeof a === "object"
        ? (a.code ?? a.number ?? a.id ?? a.name ?? "")
        : a,
    )
    .filter((x) => x !== "" && x !== null && x !== undefined)
    .join(",");
}

/** Vyberie hodnotu z multilang objektu (fallback sk-SK → sk → en-US → en → prvý dostupný). */
function pickFromObj(obj, lang) {
  if (!obj || typeof obj !== "object") return "";
  const v =
    obj[lang] ||
    obj["sk-SK"] ||
    obj["sk"] ||
    obj["en-US"] ||
    obj["en"] ||
    Object.values(obj).find((x) => typeof x === "string" && x.trim());
  return typeof v === "string" ? v : "";
}

/**
 * Názov jedla. Default = NÁZOV V MENU (title), fallback webTitle (PDF/CSV).
 * preferWeb=true → „Názov pre web" (webTitle) prvý, fallback title (Mailchimp / web export).
 */
function pickWebName(product, lang, preferWeb = false) {
  const menuName = pickFromObj(product?.title, lang);
  const webName = pickFromObj(product?.webTitle, lang);
  if (preferWeb) {
    if (webName && webName.trim() !== "") return webName;
    return menuName;
  }
  if (menuName && menuName.trim() !== "") return menuName;
  return webName;
}

/**
 * Reťazec jedla pre tlač: "300 g Názov (1,3,7)" — váha vpredu, alergény v zátvorke za,
 * rovnako ako v referenčnom lístku. Prázdny string ak jedlo nemá byť na webe alebo nemá názov.
 */
function buildDishString(product, lang, preferWeb = false) {
  if (!product) return "";
  // Do tlače idú len produkty označené „zobraziť na webe".
  if (product.showOnWeb === false) return "";
  const name = pickWebName(product, lang, preferWeb);
  if (!name.trim()) return "";
  const weight = formatWeightVolume(product);
  const allergens = formatAllergens(product.allergens);
  let s = name.trim();
  if (weight) s = `${weight} ${s}`;
  if (allergens) s = `${s} (${allergens})`;
  return s;
}

/**
 * Postaví `week` z DSOK menu. Prázdne dni (bez polievok aj jedál) sa vynechajú.
 * @param {object} menu - DSOK menu (days, productsById)
 * @param {object} [opts]
 * @param {string} [opts.lang] - jazyk názvov (napr. "sk-SK")
 * @param {(id: string) => object|null} [opts.resolveProduct] - vráti (obohatený) produkt
 */
export function buildWeekFromMenu(menu, opts = {}) {
  const lang = opts.lang || "sk-SK";
  const preferWeb = opts.preferWeb === true;
  const resolveProduct =
    typeof opts.resolveProduct === "function"
      ? opts.resolveProduct
      : (id) => menu?.productsById?.[id] || null;

  const daysOut = [];
  DAYS.forEach((dayKey) => {
    const d = menu?.days?.[dayKey];
    if (!d) return;
    const soups = (Array.isArray(d.soups) ? d.soups : [])
      .map((id) => buildDishString(resolveProduct(id), lang, preferWeb))
      .filter(Boolean);
    const items = (Array.isArray(d.meals) ? d.meals : [])
      .map((id) => buildDishString(resolveProduct(id), lang, preferWeb))
      .filter(Boolean);
    if (soups.length === 0 && items.length === 0) return;
    const dateObj = toDate(d.date);
    daysOut.push({
      name: DAY_NAMES_SK[dayKey] || dayKey,
      date: dateObj ? format(dateObj, "dd.MM.yyyy") : "",
      soups,
      items,
    });
  });

  const dated = daysOut.filter((x) => x.date);
  const week =
    dated.length > 0
      ? `${dated[0].date} - ${dated[dated.length - 1].date}`
      : "";

  return { week, days: daysOut };
}

/** Názov listu podľa typu menu. */
export function sheetTitleForMenuType(menuType) {
  switch (menuType) {
    case "breakfast":
      return "Raňajkové menu";
    case "lunch":
      return "Obedové menu";
    case "dinner":
      return "Večerné menu";
    case "night":
      return "Nočné menu";
    default:
      return "Denné menu";
  }
}

/**
 * Default SK texty pre tlač (informácia + zoznam alergénov 1–14, znenie podľa výnosu MZ/MPRV SR).
 * Prevzaté z referenčného balíka. Modálne labely sú v SK.
 */
export const DEFAULT_PRINT_COPY_SK = {
  modalClose: "Zavrieť",
  modalPrint: "Tlačiť",
  infoHeading: "Informácia",
  infoParagraphs: [
    "Čísla v zátvorke pri jednotlivých položkách uvádzajú možné alergény.",
    "Zmena jedálneho lístka je vyhradená!!!",
    "Váha jedla na 1 porciu je uvedená po tepelnej úprave.",
  ],
  allergenHeading:
    "Podľa výnosu vydaného ministerstvom zdravotníctva a min. pôdohospodárstva Slovenskej republiky uvádzame zoznam alergénov:",
  allergens: [
    { code: "1", text: "Obilniny obsahujúce lepok a výrobky z nich" },
    { code: "2", text: "Kôrovce a výrobky z nich" },
    { code: "3", text: "Vajcia a výrobky z nich" },
    { code: "4", text: "Ryby a výrobky z nich" },
    { code: "5", text: "Arašidy a výrobky z nich" },
    { code: "6", text: "Sója a výrobky z nej" },
    { code: "7", text: "Mlieko a výrobky z neho (vrátane laktózy)" },
    { code: "8", text: "Orechy a výrobky z nich" },
    { code: "9", text: "Zeler a výrobky z neho" },
    { code: "10", text: "Horčica a výrobky z nej" },
    { code: "11", text: "Sezamové semená a výrobky z nich" },
    {
      code: "12",
      text: "Oxid siričitý a siričitany (10 mg/kg alebo 10 mg/l a viac)",
    },
    { code: "13", text: "Vlčí bob a výrobky z neho" },
    { code: "14", text: "Mäkkýše a výrobky z nich" },
  ],
};
